import { createContext, useCallback, useContext, useMemo } from 'react';
import type { ResellerTenantView } from '@/api/resellerAdmin';
import { createTenantApi, type TenantApi } from '@/api/tenantApi';

/**
 * The open tenant plus the layout's callbacks (optional so a test can provide a bare tenant):
 * `reload` soft-refetches it; `onApiError` sees every per-tenant call's rejection.
 */
export type TenantContextValue = ResellerTenantView & {
  reload?: () => Promise<void>;
  onApiError?: (err: unknown) => void;
};

export const TenantContext = createContext<TenantContextValue | null>(null);

function useTenantContext(hook: string): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error(`${hook} must be used within a BusinessLayout`);
  return ctx;
}

/** The open tenant (the layout's `GET /tenant`). Call within a `BusinessLayout`. */
export function useTenant(): ResellerTenantView {
  const { reload: _reload, onApiError: _onApiError, ...tenant } = useTenantContext('useTenant');
  return tenant;
}

/**
 * The open tenant's currency (ISO 4217), or `''` when the tenant view carries none. Never a
 * default: a blank currency formats through `moneyIn` as "unavailable" (the money rule, DATA-003).
 */
export function useTenantCurrency(): string {
  return useTenant().currency;
}

/** Soft-refetch the open tenant without blanking the layout. */
export function useTenantReload(): () => Promise<void> {
  const ctx = useTenantContext('useTenantReload');
  return ctx.reload ?? (async () => undefined);
}

/**
 * The per-tenant tmm-api operations, bound to the open tenant's slug (H §6.2). Every rejection is
 * shown to the layout's `onApiError` (the shell's role-forbidden handling) and rethrown, so
 * the calling screen still handles it as before.
 */
export function useTenantApi(): TenantApi {
  const { slug, onApiError } = useTenantContext('useTenantApi');
  return useMemo(() => {
    const api = createTenantApi(slug);
    if (!onApiError) return api;
    const observed = Object.entries(api).map(([name, op]) => [
      name,
      (...args: unknown[]) =>
        (op as (...a: unknown[]) => Promise<unknown>)(...args).catch((err: unknown) => {
          onApiError(err);
          throw err;
        }),
    ]);
    return Object.fromEntries(observed) as TenantApi;
  }, [slug, onApiError]);
}

/** In-app path of a business page (or sub-path): `/business/<suffix>`. */
export function useTenantLink(): (suffix?: string) => string {
  useTenantContext('useTenantLink');
  return useCallback((suffix?: string) => (suffix ? `/business/${suffix}` : '/business'), []);
}
